<template>
    <div class="shop">
        <div v-if="isSending" class="sending-overlay" aria-live="polite" aria-busy="true">
            <div class="sending-spinner" aria-hidden="true"></div>
        </div>
        <form v-if="!successMessage && isCartItems()" @submit.prevent="validateForm">
            <div class="header">
                <p class="selected-category">{{ translations.title }}</p>
                <span>{{ translations.description }}</span>
                <p class="warning">{{ translations.option_delivery }}</p>
                <div class="radio-group">
                    <label class="radio-option">
                        <input type="radio" v-model="selectedOption" value="pickup" />
                        <span class="custom-radio"></span>
                        {{ translations.pickup }}
                    </label>
                    <label class="radio-option">
                        <input type="radio" v-model="selectedOption" value="delivery" />
                        <span class="custom-radio"></span>
                        {{ translations.delivery }}
                    </label>
                </div>
                <p ref="radioErrorElement" v-if="radioError && !selectedOption" class="error-message">{{
                    translations.errors_form.option_delivery }}</p>

                <p class="trans">
                    <i>{{ translations.method_delivery }}</i>
                </p>
                <p>{{ translations.pickup_address_title }}</p>
                <p>{{ translations.pickup_address }}</p>
                <p class="warning">{{ translations.set_delivery_pickup_date }}</p>
                <div class="delivery-pickup-form">
                    <div class="form-grid">
                        <div class="form-group">
                            <Datepicker :placeholder="translations.delivery_date" :class="$style.input"
                                v-model="cartStore.add_information.deliveryDate"
                                :inputFormat="translations.input_format" :locale="locale" :lowerLimit="new Date()" />
                            <p ref="radioErrorElement" v-if="radioError && !cartStore.add_information.deliveryDate"
                                class="error-message">{{
                                    translations.errors_form.date_delivery }}</p>
                        </div>
                        <div class="form-group">
                            <div class="custom-time-picker">
                                <input type="text" v-model="cartStore.add_information.deliveryTime"
                                    @focus="showDeliveryDropdown = true" @blur="hideDeliveryDropdown"
                                    placeholder="HH:MM" />
                                <ul v-if="showDeliveryDropdown" class="dropdown">
                                    <li v-for="time in times" :key="time" @mousedown.prevent="selectDeliveryTime(time)">
                                        {{ time }}
                                    </li>
                                </ul>
                                <p ref="radioErrorElement" v-if="radioError && !cartStore.add_information.deliveryTime"
                                    class="error-message">{{ translations.errors_form.time_delivery }}</p>
                            </div>
                        </div>
                        <div class="form-group">
                            <Datepicker class="calendar" v-model="cartStore.add_information.pickupDate"
                                :placeholder="translations.pickup_date" :class="$style.input"
                                :inputFormat="translations.input_format" :locale="locale"
                                :lowerLimit="pickupLowerLimit" />
                            <p ref="radioErrorElement" v-if="radioError && !cartStore.add_information.pickupDate"
                                class="error-message">{{
                                    translations.errors_form.date_pickup }}</p>
                            <p v-if="dateRangeError" class="error-message">{{ dateRangeError }}</p>
                        </div>
                        <div class="form-group">
                            <div class="custom-time-picker">
                                <input type="text" v-model="cartStore.add_information.pickupTime"
                                    @focus="showPickupDropdown = true" @blur="hidePickupDropdown" placeholder="HH:MM" />
                                <ul v-if="showPickupDropdown" class="dropdown">
                                    <li v-for="time in times" :key="time" @mousedown.prevent="selectPickupTime(time)">
                                        {{ time }}
                                    </li>
                                </ul>
                                <p ref="radioErrorElement" v-if="radioError && !cartStore.add_information.pickupTime"
                                    class="error-message">{{ translations.errors_form.time_pickup }}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <p class="warning">{{ translations.data_personal }}</p>
                <div class="form-container">
                    <div class="form-grid">
                        <div class="form-group">
                            <input type="text" id="firstName" v-model="firstName" :placeholder="translations.name" />
                            <p ref="radioErrorElement" v-if="radioError && !firstName" class="error-message">
                                {{ translations.errors_form.name }}</p>
                        </div>
                        <div class="form-group">
                            <input type="text" id="lastName" v-model="lastName" :placeholder="translations.surname" />
                            <p ref="radioErrorElement" v-if="radioError && !lastName" class="error-message">{{
                                translations.errors_form.surname }}</p>
                        </div>
                        <div class="form-group full-width">
                            <input type="text" id="companyName" v-model="companyName"
                                :placeholder="translations.company" />
                        </div>
                        <div class="form-group full-width">
                            <input type="text" id="companyNip" :placeholder="translations.nip" v-model="companyNip" />
                            <p v-if="radioError && companyNip && !isValidNip(companyNip)" class="error-message">{{
                                translations.errors_form.error_nip }}</p>
                        </div>
                        <div class="form-group full-width">
                            <input type="text" id="companyAddress" :placeholder="translations.company_address"
                                v-model="companyAddress" />
                        </div>
                        <div class="form-group">
                            <input type="text" id="city" :placeholder="translations.city" v-model="city" />
                            <p ref="radioErrorElement" v-if="radioError && !city" class="error-message">{{
                                translations.errors_form.city }}</p>
                        </div>
                        <div class="form-group">
                            <input type="text" id="postalCode" :placeholder="translations.code_postal"
                                v-model="postalCode" />
                            <p ref="radioErrorElement" v-if="radioError && !postalCode" class="error-message">{{
                                translations.errors_form.code_postal }}</p>
                        </div>
                        <div class="form-group">
                            <input type="email" id="email" :placeholder="translations.email" v-model="email" />
                            <p v-if="radioError && !email" class="error-message">{{ translations.errors_form.email }}
                            </p>
                        </div>
                        <div class="form-group">
                            <input type="text" id="phoneNumber" :placeholder="translations.number"
                                v-model="phoneNumber" />
                            <p v-if="radioError && !phoneNumber" class="error-message">{{
                                translations.errors_form.number }}</p>
                            <p v-if="radioError && phoneNumber && !/^\d{9}$/.test(phoneNumber)" class="error-message">{{
                                translations.errors_form.error_number }}</p>
                        </div>
                    </div>
                </div>
                <p class="warning">{{ translations.data_delivery }}</p>
                <div class="form-container">
                    <div class="form-grid">
                        <div class="form-group">
                            <input type="text" id="firstNameDelivery" :placeholder="translations.name"
                                v-model="firstNameDelivery" :disabled="selectedOption !== 'delivery'" />
                            <p ref="firstNameDeliveryError"
                                v-if="radioError && !firstNameDelivery && selectedOption === 'delivery'"
                                class="error-message">{{ translations.errors_form.name_delivery }}</p>
                        </div>
                        <div class="form-group">
                            <input type="text" id="lastNameDelivery" :placeholder="translations.surname"
                                v-model="lastNameDelivery" :disabled="selectedOption !== 'delivery'" />
                            <p ref="surNameDeliveryError"
                                v-if="radioError && !lastNameDelivery && selectedOption === 'delivery'"
                                class="error-message">
                                {{ translations.errors_form.surname_delivery }}</p>
                        </div>
                        <div class="form-group full-width">
                            <input type="text" id="companyNameDelivery" :placeholder="translations.company"
                                v-model="companyNameDelivery" :disabled="selectedOption !== 'delivery'" />
                        </div>
                        <div class="form-group full-width">
                            <input type="text" id="companyAddressDelivery" :placeholder="translations.address_delivery"
                                v-model="companyAddressDelivery" :disabled="selectedOption !== 'delivery'" />
                            <p ref="radioErrorElement"
                                v-if="radioError && !companyAddressDelivery && selectedOption === 'delivery'"
                                class="error-message">{{
                                    translations.errors_form.company_address_delivery }}</p>
                        </div>
                        <div class="form-group">
                            <input type="text" id="cityDelivery" :placeholder="translations.city" v-model="cityDelivery"
                                :disabled="selectedOption !== 'delivery'" />
                            <p ref="radioErrorElement"
                                v-if="radioError && !cityDelivery && selectedOption === 'delivery'"
                                class="error-message">{{
                                    translations.errors_form.city_delivery }}</p>
                        </div>
                        <div class="form-group">
                            <input type="text" id="postalCodeDelivery" :placeholder="translations.code_postal"
                                v-model="postalCodeDelivery" :disabled="selectedOption !== 'delivery'" />
                            <p ref="radioErrorElement"
                                v-if="radioError && !postalCodeDelivery && selectedOption === 'delivery'"
                                class="error-message">{{
                                    translations.errors_form.code_postal_delivery }}</p>
                        </div>
                        <div class="form-group">
                            <input type="text" id="phoneNumberDelivery" :placeholder="translations.number"
                                v-model="phoneNumberDelivery" :disabled="selectedOption !== 'delivery'" />
                            <p v-if="radioError && !phoneNumberDelivery && selectedOption === 'delivery'"
                                class="error-message">{{
                                    translations.errors_form.number_delivery }}</p>
                            <p v-if="radioError && phoneNumberDelivery && !/^\d{9}$/.test(phoneNumberDelivery) && selectedOption === 'delivery'"
                                class="error-message">{{
                                    translations.errors_form.error_number_delivery }}</p>
                        </div>
                    </div>
                </div>
                <div class="checkbox-group">
                    <label class="custom-checkbox">
                        <input type="checkbox" v-model="rodo_accept_option" />
                        <span class="checkmark"></span>
                        {{ translations.rodo_accept }}
                    </label>
                    <p v-if="radioError && !rodo_accept_option" class="error-message">{{
                        translations.errors_form.rodo_accept }}</p>
                    <label class="custom-checkbox">
                        <input type="checkbox" v-model="statute_accept_option" />
                        <span class="checkmark"></span>
                        {{ translations.statute_accept }}
                    </label>
                    <p v-if="radioError && !statute_accept_option" class="error-message">{{
                        translations.errors_form.statute_accept }}</p>
                </div>
                <div class="cart-summary">
                    <div class="summary">
                        <p class="sum">{{ translations.sum }}</p>
                        <p class="price">{{ cartStore.totalNet }} {{ translations.currency }}</p>
                        <p class="price">{{ cartStore.totalBrutto }} {{ translations.currency }}</p>
                    </div>
                    <p v-if="shouldRequireRecaptcha()" class="captcha-note">
                        To kolejne wysłanie dla tego e-maila w ciągu 10 minut. Zaznacz reCAPTCHA.
                    </p>
                    <div v-show="shouldRequireRecaptcha()" class="recaptcha-wrapper">
                        <div ref="recaptchaContainer" class="recaptcha-box"></div>
                    </div>
                    <p v-if="recaptchaError" class="error-message">{{ recaptchaError }}</p>
                    <p v-if="submitError" class="error-message">{{ submitError }}</p>
                    <button type="submit" :disabled="isSending">{{ translations.subbmit }}</button>
                </div>
            </div>
        </form>
        <div class="success_message" v-if="successMessage && !isCartItems()">
            <div class="success-content">
                <p class="success-title">{{ translations.success.title }}</p>
                <p class="success-subtitle">{{ translations.success.desc }}</p>
                <p class="success-subtitle-1">{{ translations.success.desc_1 }}</p>
                <button @click="redirectToHome" class="success-button">{{ translations.success.button }}</button>
            </div>
            <div class="success-image">
                <img src="@/assets/shop/success.webp" alt="Obraz przedstawiający zapytanie wysłane">
            </div>
        </div>
        <div v-else-if="!successMessage && !isCartItems()" class="success_message">
            <div class="success-content">
                <p class="success-title">{{ translations.error.title }}</p>
                <p class="success-subtitle">{{ translations.error.desc }}</p>
            </div>
            <div class="success-image">
                <img src="@/assets/shop/success.webp" alt="Obraz przedstawiający zapytanie wysłane">
            </div>
        </div>
    </div>
    <AppFooter />
</template>

<script>
import translationsData from '@/assets/text_lang/translations.json';
import { useLanguageStore } from '@/theme';
import { useCartStore } from '../../store/cart';

import { ref, computed, watch } from 'vue';

import { pl, enUS } from 'date-fns/locale';
import Datepicker from 'vue3-datepicker';
import AppFooter from './FootBar.vue';

import axios from 'axios';
import { useHead } from '@vueuse/head';




export default {
    name: 'SummaryCartPage',
    components: {
        Datepicker,
        AppFooter
    },
    data() {
        return {
            selectedOption: '', // Przechowywanie wybranej opcji
            additionalInfo: '', // Przechowywanie wartości textarea
            firstName: '',
            firstNameDelivery: '',
            lastName: '',
            lastNameDelivery: '',
            companyName: '',
            companyNameDelivery: '',
            companyNip: '',
            companyAddress: '',
            companyAddressDelivery: '',
            city: '',
            cityDelivery: '',
            postalCode: '',
            postalCodeDelivery: '',
            email: '',
            phoneNumber: '',
            phoneNumberDelivery: '',

            rodo_accept_option: false,
            statute_accept_option: false,

            showDeliveryDropdown: false,
            showPickupDropdown: false,
            times: this.generateTimeOptions(),
            defaultOrderTime: '08:00',
            defaultPickupTime: '09:00',

            radioError: '',
            dateRangeError: '',

            successMessage: false,
            isSending: false,
            recaptchaSiteKey: process.env.VUE_APP_RECAPTCHA_SITE_KEY || '',
            recaptchaToken: '',
            recaptchaError: '',
            submitError: '',
            recaptchaScriptPromise: null,
            recaptchaWidgetId: null,
            recaptchaHistoryStorageKey: 'summary_cart_recent_email_submissions',
            recaptchaRepeatWindowMs: 10 * 60 * 1000,
            forceRecaptchaByBackend: false,
        };
    },
    setup() {
        useHead({
            title: 'Podsumowanie koszyka',
            meta: [
                {
                    name: 'description',
                    content: 'Uzupełnij dane i wyślij zapytanie o wynajem sprzętu barowego. Cocktail Service realizuje zamówienia w Warszawie i na terenie całej Polski.'
                },
                {
                    property: 'og:title',
                    content: 'Podsumowanie koszyka | Cocktail Service'
                },
                {
                    property: 'og:description',
                    content: 'Zweryfikuj zamówienie, wybierz dostawę lub odbiór i wyślij zapytanie o dostępność terminu.'
                }
            ]
        });
        const languageStore = useLanguageStore();
        const cartStore = useCartStore();
        const translations = computed(() => {
            return translationsData[languageStore.currentLanguage]["cart_summary"];
        });
        const currentLanguage = computed(() => languageStore.currentLanguage);
        const locale = ref(enUS);
        watch(currentLanguage, (newLang) => {
            if (newLang === 'pl') {
                locale.value = pl;
            } else {
                locale.value = enUS;
            }
        }, { immediate: true });

        return {
            translations,
            cartStore,
            locale,
        };
    },
    computed: {
        translatedQuantity() {
            return this.quantity === 1 ? this.translations.pc : this.translations.pcs;
        },
        pickupLowerLimit() {
            const deliveryDate = this.toValidDate(this.cartStore.add_information.deliveryDate);
            return deliveryDate || new Date();
        },
    },
    watch: {
        email() {
            this.recaptchaError = '';
            this.submitError = '';
            this.resetRecaptchaWidget();
            this.syncRecaptchaRequirement();
        },
        'cartStore.add_information.deliveryDate'() {
            this.validateDateOrder();
        },
        'cartStore.add_information.pickupDate'() {
            this.validateDateOrder();
        },
        'cartStore.add_information.deliveryTime'() {
            this.validateDateOrder();
        },
        'cartStore.add_information.pickupTime'() {
            this.validateDateOrder();
        },
    },
    methods: {
        toValidDate(value) {
            if (value instanceof Date && !Number.isNaN(value.getTime())) {
                return value;
            }
            if (typeof value !== 'string' || value.trim() === '') {
                return null;
            }
            // Date-only strings (YYYY-MM-DD) are parsed as UTC by spec,
            // causing timezone shift — force local time interpretation
            const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
            const parsedDate = isDateOnly
                ? new Date(value.trim().replace(/-/g, '/'))
                : new Date(value);
            return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
        },
        normalizeStoreDates() {
            const normalizedDeliveryDate = this.toValidDate(this.cartStore.add_information.deliveryDate);
            const normalizedPickupDate = this.toValidDate(this.cartStore.add_information.pickupDate);
            this.cartStore.add_information.deliveryDate = normalizedDeliveryDate;
            this.cartStore.add_information.pickupDate = normalizedPickupDate;
        },
        setDefaultTimesIfMissing() {
            if (!this.cartStore.add_information.deliveryTime) {
                this.cartStore.add_information.deliveryTime = this.defaultOrderTime;
            }
            if (!this.cartStore.add_information.pickupTime) {
                this.cartStore.add_information.pickupTime = this.defaultPickupTime;
            }
        },
        normalizeToDayTimestamp(value) {
            const date = this.toValidDate(value);
            if (!date) {
                return null;
            }
            return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        },
        toDateTimeTimestamp(date, time) {
            const dayTimestamp = this.normalizeToDayTimestamp(date);
            if (dayTimestamp === null) return null;

            const [hours = 0, minutes = 0] = (time || '00:00').split(':').map(Number);
            return dayTimestamp + hours * 3600000 + minutes * 60000;
        },
        validateDateOrder() {
            const deliveryTs = this.toDateTimeTimestamp(
                this.cartStore.add_information.deliveryDate,
                this.cartStore.add_information.deliveryTime,
            );
            const pickupTs = this.toDateTimeTimestamp(
                this.cartStore.add_information.pickupDate,
                this.cartStore.add_information.pickupTime,
            );

            if (deliveryTs === null || pickupTs === null) {
                this.dateRangeError = '';
                return true;
            }

            const isValidOrder = pickupTs > deliveryTs;
            this.dateRangeError = isValidOrder
                ? ''
                : 'Data zakończenia musi być inna i późniejsza niż data rozpoczęcia.';

            return isValidOrder;
        },

        updateDeliveryFieldsState() {
            if (this.selectedOption !== 'delivery') {
                this.firstNameDelivery = '';
                this.lastNameDelivery = '';
                this.companyNameDelivery = '';
                this.companyAddressDelivery = '';
                this.cityDelivery = '';
                this.postalCodeDelivery = '';
            }
        },
        redirectToHome() {
            this.successMessage = false;
            this.$router.push('/'); // Przekierowanie do strony głównej
        },
        generateTimeOptions() {
            const options = [];
            for (let h = 0; h < 24; h++) {
                for (let m = 0; m < 60; m += 30) {
                    const hour = String(h).padStart(2, '0');
                    const minute = String(m).padStart(2, '0');
                    options.push(`${hour}:${minute}`);
                }
            }
            return options;
        },
        selectDeliveryTime(time) {
            this.cartStore.add_information.deliveryTime = time;
            this.showDeliveryDropdown = false;
        },
        hideDeliveryDropdown() {
            setTimeout(() => {
                this.showDeliveryDropdown = false;
            }, 200);
        },
        selectPickupTime(time) {
            this.cartStore.add_information.pickupTime = time;
            this.showPickupDropdown = false;
        },
        hidePickupDropdown() {
            setTimeout(() => {
                this.showPickupDropdown = false;
            }, 200);
        },
        isCartItems() {
            return this.cartStore.items.length > 0;
        },
        isValidNip(nip) {
            const cleaned = nip.replace(/[\s-]/g, '');
            if (!/^\d{10}$/.test(cleaned)) return false;
            const digits = cleaned.split('').map(Number);
            if (digits[0] === 0) return false;
            const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
            const sum = weights.reduce((acc, w, i) => acc + w * digits[i], 0);
            const checksum = sum % 11;
            return checksum !== 10 && checksum === digits[9];
        },
        normalizeEmail(value) {
            return String(value || '').trim().toLowerCase();
        },
        getRecaptchaHistory() {
            try {
                const raw = localStorage.getItem(this.recaptchaHistoryStorageKey);
                if (!raw) {
                    return {};
                }
                const parsed = JSON.parse(raw);
                if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                    return {};
                }
                return parsed;
            } catch (error) {
                console.error('Nie udało się odczytać historii reCAPTCHA:', error);
                return {};
            }
        },
        saveRecaptchaHistory(history) {
            try {
                localStorage.setItem(this.recaptchaHistoryStorageKey, JSON.stringify(history));
            } catch (error) {
                console.error('Nie udało się zapisać historii reCAPTCHA:', error);
            }
        },
        shouldRequireRecaptcha() {
            if (this.forceRecaptchaByBackend) {
                return true;
            }
            const emailKey = this.normalizeEmail(this.email);
            if (!emailKey) {
                return false;
            }

            const history = this.getRecaptchaHistory();
            const lastSubmissionAt = Number(history[emailKey] || 0);
            if (!lastSubmissionAt) {
                return false;
            }

            const isWithinRepeatWindow = Date.now() - lastSubmissionAt < this.recaptchaRepeatWindowMs;
            if (!isWithinRepeatWindow) {
                delete history[emailKey];
                this.saveRecaptchaHistory(history);
                return false;
            }
            return true;
        },
        async enforceRecaptchaFromBackend(reason) {
            const reasonText = String(reason || '').toLowerCase();
            const shouldForceRecaptcha = reasonText === 'missing_token' || reasonText === 'captcha_failed';
            if (!shouldForceRecaptcha) {
                return;
            }
            this.forceRecaptchaByBackend = true;
            try {
                await this.syncRecaptchaRequirement();
            } catch (error) {
                console.error('Nie udało się wymusić renderowania reCAPTCHA:', error);
            }
        },
        registerSuccessfulSubmissionEmail() {
            const emailKey = this.normalizeEmail(this.email);
            if (!emailKey) {
                return;
            }

            const history = this.getRecaptchaHistory();
            history[emailKey] = Date.now();
            this.saveRecaptchaHistory(history);
        },
        async syncRecaptchaRequirement() {
            if (!this.shouldRequireRecaptcha()) {
                this.recaptchaError = '';
                this.resetRecaptchaWidget();
                return;
            }
            try {
                await this.renderRecaptchaWidget();
            } catch (error) {
                console.error('Nie udało się przygotować reCAPTCHA:', error);
                if (error && error.message === 'missing-recaptcha-site-key') {
                    this.recaptchaError = 'Brak klucza reCAPTCHA. Skonfiguruj VUE_APP_RECAPTCHA_SITE_KEY.';
                    return;
                }
                this.recaptchaError = 'Nie udało się załadować reCAPTCHA. Sprawdź blokowanie skryptów (AdBlock/ochrona prywatności) i odśwież stronę.';
            }
        },
        async waitForRecaptchaApi(maxWaitMs = 10000) {
            const startTime = Date.now();
            while (Date.now() - startTime < maxWaitMs) {
                if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
                    return;
                }
                await new Promise((resolve) => setTimeout(resolve, 150));
            }
            throw new Error('recaptcha-timeout');
        },
        async ensureRecaptchaLoaded() {
            if (!this.recaptchaSiteKey) {
                throw new Error('missing-recaptcha-site-key');
            }
            if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
                return;
            }
            if (this.recaptchaScriptPromise) {
                try {
                    await this.recaptchaScriptPromise;
                } catch (error) {
                    this.recaptchaScriptPromise = null;
                    throw error;
                }
                await this.waitForRecaptchaApi();
                return;
            }
            this.recaptchaScriptPromise = new Promise((resolve, reject) => {
                const existingScript = document.querySelector('script[data-recaptcha="v2-order"]');
                if (existingScript) {
                    const timeoutId = setTimeout(() => {
                        reject(new Error('recaptcha-timeout'));
                    }, 10000);
                    const checkReady = () => {
                        if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
                            clearTimeout(timeoutId);
                            resolve();
                        } else {
                            setTimeout(checkReady, 200);
                        }
                    };
                    checkReady();
                    return;
                }

                const script = document.createElement('script');
                script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
                script.async = true;
                script.defer = true;
                script.dataset.recaptcha = 'v2-order';
                script.onload = () => resolve();
                script.onerror = () => reject(new Error('recaptcha-load-failed'));
                document.head.appendChild(script);
            });

            try {
                await this.recaptchaScriptPromise;
            } catch (error) {
                this.recaptchaScriptPromise = null;
                throw error;
            }
            await this.waitForRecaptchaApi();
        },
        async renderRecaptchaWidget() {
            if (!this.shouldRequireRecaptcha()) {
                return;
            }
            await this.ensureRecaptchaLoaded();
            await this.$nextTick();

            if (!this.$refs.recaptchaContainer) {
                throw new Error('recaptcha-container-not-found');
            }

            const hasRenderedIframe = Boolean(this.$refs.recaptchaContainer.querySelector('iframe'));
            if (this.recaptchaWidgetId !== null && !hasRenderedIframe) {
                this.recaptchaWidgetId = null;
            }

            if (this.recaptchaWidgetId === null) {
                this.recaptchaWidgetId = window.grecaptcha.render(this.$refs.recaptchaContainer, {
                    sitekey: this.recaptchaSiteKey,
                    callback: this.onRecaptchaSolved,
                    'expired-callback': this.onRecaptchaExpired,
                    'error-callback': this.onRecaptchaError,
                });
            }
        },
        onRecaptchaSolved(token) {
            this.recaptchaToken = token || '';
            this.recaptchaError = '';
            this.submitError = '';
        },
        onRecaptchaExpired() {
            this.recaptchaToken = '';
            this.recaptchaError = 'Weryfikacja reCAPTCHA wygasła. Proszę zaznaczyć ponownie.';
        },
        onRecaptchaError() {
            this.recaptchaToken = '';
            this.recaptchaError = 'Błąd reCAPTCHA. Proszę odświeżyć stronę i spróbować ponownie.';
        },
        resetRecaptchaWidget() {
            if (this.recaptchaWidgetId !== null && window.grecaptcha && typeof window.grecaptcha.reset === 'function') {
                window.grecaptcha.reset(this.recaptchaWidgetId);
            }
            this.recaptchaToken = '';
        },
        safeParseJson(value) {
            if (typeof value !== 'string') {
                return value;
            }
            try {
                return JSON.parse(value);
            } catch (error) {
                return value;
            }
        },
        normalizeBackendEnvelope(data) {
            const parsed = this.safeParseJson(data);
            if (!parsed || typeof parsed !== 'object') {
                return { statusCode: null, payload: parsed };
            }
            if (!('statusCode' in parsed) && !('body' in parsed)) {
                return { statusCode: null, payload: parsed };
            }
            const normalizedStatusCode = Number(parsed.statusCode || 0) || null;
            const normalizedPayload = this.safeParseJson(parsed.body);
            return { statusCode: normalizedStatusCode, payload: normalizedPayload };
        },
        getBackendRejectReason(response) {
            const { payload } = this.normalizeBackendEnvelope(response?.data);
            if (!payload || typeof payload !== 'object') {
                return '';
            }
            return payload.reason || payload.error || '';
        },
        mapBackendRejectReason(reason) {
            const reasonText = String(reason || '').toLowerCase();
            if (reasonText === 'missing_token') {
                return 'Brak tokenu reCAPTCHA. Zaznacz pole reCAPTCHA i wyślij ponownie.';
            }
            if (reasonText === 'captcha_failed') {
                return 'Weryfikacja reCAPTCHA nie powiodła się. Spróbuj ponownie.';
            }
            if (reasonText === 'invalid_hostname') {
                return 'Token reCAPTCHA pochodzi z nieprawidłowej domeny.';
            }
            return 'Wysłanie zostało odrzucone przez zabezpieczenia. Sprawdź reCAPTCHA i spróbuj ponownie.';
        },
        isBackendSubmissionAccepted(response) {
            if (!response || response.status < 200 || response.status >= 300) {
                return false;
            }
            const { statusCode, payload } = this.normalizeBackendEnvelope(response.data);
            if (statusCode !== null && statusCode >= 400) {
                return false;
            }
            const data = payload;
            if (!data || typeof data !== 'object') {
                return true;
            }
            if ('error' in data && data.error) {
                return false;
            }
            if ('ok' in data) {
                return Boolean(data.ok);
            }
            return true;
        },
        async resolveRecaptchaToken() {
            if (!this.shouldRequireRecaptcha()) {
                this.recaptchaError = '';
                return '';
            }
            this.recaptchaError = '';
            try {
                await this.renderRecaptchaWidget();
                if (!this.recaptchaToken) {
                    this.recaptchaError = 'Proszę zaznaczyć pole reCAPTCHA.';
                    return '';
                }
                return this.recaptchaToken;
            } catch (error) {
                console.error('reCAPTCHA verification failed:', error);
                if (error && error.message === 'missing-recaptcha-site-key') {
                    this.recaptchaError = 'Brak klucza reCAPTCHA. Skonfiguruj VUE_APP_RECAPTCHA_SITE_KEY.';
                    return '';
                }
                if (error && (error.message === 'recaptcha-load-failed' || error.message === 'recaptcha-timeout' || error.message === 'recaptcha-container-not-found')) {
                    this.recaptchaError = 'reCAPTCHA nie załadowała się. Wyłącz blokowanie skryptów (AdBlock/ochrona prywatności), odśwież stronę i spróbuj ponownie.';
                    return '';
                }
                this.recaptchaError = 'Nie udało się zweryfikować reCAPTCHA. Proszę spróbować ponownie.';
                return '';
            }
        },
        async validateForm() {
            let valid = true;
            this.radioError = '';
            this.dateRangeError = '';
            this.recaptchaError = '';
            this.submitError = '';
            if (!this.selectedOption) {
                this.radioError = "error";
                valid = false;
                this.$nextTick(() => {
                    if (this.$refs.radioErrorElement) {
                        this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            }
            if (!this.cartStore.add_information.deliveryDate) {
                this.radioError = 'Proszę wybrać datę dostawy.';
                valid = false;
                this.$nextTick(() => {
                    if (this.$refs.radioErrorElement) {
                        this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            }
            if (!this.cartStore.add_information.deliveryTime) {
                this.radioError = 'Proszę wybrać godzinę dostawy.';
                valid = false;
                this.$nextTick(() => {
                    if (this.$refs.radioErrorElement) {
                        this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            }
            if (!this.cartStore.add_information.pickupDate) {
                this.radioError = 'Proszę wybrać datę odbioru.';
                valid = false;
                this.$nextTick(() => {
                    if (this.$refs.radioErrorElement) {
                        this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            }
            if (!this.cartStore.add_information.pickupTime) {
                this.radioError = 'Proszę wybrać godzinę odbioru.';
                valid = false;
                this.$nextTick(() => {
                    if (this.$refs.radioErrorElement) {
                        this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            }
            if (!this.validateDateOrder()) {
                this.radioError = 'Proszę poprawić zakres dat.';
                valid = false;
                this.$nextTick(() => {
                    if (this.$refs.radioErrorElement) {
                        this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            }
            console.log("firstName", this.firstName);
            if (!this.firstName) {
                this.radioError = 'Proszę podać imię.';
                valid = false;
                this.$nextTick(() => {
                    if (this.$refs.radioErrorElement) {
                        this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            }
            if (!this.lastName) {
                this.radioError = 'Proszę podać nazwisko.';
                valid = false;
                this.$nextTick(() => {
                    if (this.$refs.radioErrorElement) {
                        this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            }
            if (!this.city) {
                this.radioError = 'Proszę podać miasto.';
                valid = false;
                this.$nextTick(() => {
                    if (this.$refs.radioErrorElement) {
                        this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            }
            if (!this.postalCode) {
                this.radioError = 'Proszę podać kod pocztowy.';
                valid = false;
                this.$nextTick(() => {
                    if (this.$refs.radioErrorElement) {
                        this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            }
            if (!this.email) {
                this.radioError = 'Proszę podać adres e-mail.';
                valid = false;
                this.$nextTick(() => {
                    if (this.$refs.radioErrorElement) {
                        this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            }

            if (!this.phoneNumber) {
                this.radioError = 'Proszę podać numer telefonu.';
                valid = false;
                this.$nextTick(() => {
                    if (this.$refs.radioErrorElement) {
                        this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            }
            if (!this.phoneNumber.match(/^\d{9}$/)) {
                this.radioError = 'Proszę podać poprawny numer telefonu.';
                valid = false;
                this.$nextTick(() => {
                    if (this.$refs.radioErrorElement) {
                        this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            }
            if (this.companyNip && !this.isValidNip(this.companyNip)) {
                this.radioError = 'Proszę podać poprawny numer NIP.';
                valid = false;
                this.$nextTick(() => {
                    if (this.$refs.radioErrorElement) {
                        this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            }
            if (this.selectedOption === 'delivery') {
                if (!this.firstNameDelivery) {
                    this.radioError = 'Proszę podać imię.';
                    valid = false;
                    this.$nextTick(() => {
                        if (this.$refs.radioErrorElement) {
                            this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    });
                }

                if (!this.lastNameDelivery) {
                    this.radioError = 'Proszę podać nazwisko.';
                    valid = false;
                    this.$nextTick(() => {
                        if (this.$refs.radioErrorElement) {
                            this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    });
                }

                if (!this.companyAddressDelivery) {
                    this.radioError = 'Proszę podać adres dostawy.';
                    valid = false;
                    this.$nextTick(() => {
                        if (this.$refs.radioErrorElement) {
                            this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    });
                }

                // if (!this.companyNameDelivery) {
                //     this.radioError = 'Proszę podać nazwę firmy.';
                //     valid = false;
                //     this.$nextTick(() => {
                //         if (this.$refs.radioErrorElement) {
                //             this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                //         }
                //     });
                // }

                if (!this.cityDelivery) {
                    this.radioError = 'Proszę podać miasto.';
                    valid = false;
                    this.$nextTick(() => {
                        if (this.$refs.radioErrorElement) {
                            this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    });
                }

                if (!this.postalCodeDelivery) {
                    this.radioError = 'Proszę podać kod pocztowy.';
                    valid = false;
                    this.$nextTick(() => {
                        if (this.$refs.radioErrorElement) {
                            this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    });
                }
                if (!this.phoneNumberDelivery) {
                    this.radioError = 'Proszę podać numer telefonu.';
                    valid = false;
                    this.$nextTick(() => {
                        if (this.$refs.radioErrorElement) {
                            this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    });
                }
                if (!this.phoneNumberDelivery.match(/^\d{9}$/)) {
                    this.radioError = 'Proszę podać poprawny numer telefonu.';
                    valid = false;
                    this.$nextTick(() => {
                        if (this.$refs.radioErrorElement) {
                            this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    });
                }
            }


            if (!this.rodo_accept_option) {
                this.radioError = 'Proszę zaakceptować wszystkie wymagane zgody.';
                valid = false;
                this.$nextTick(() => {
                    if (this.$refs.radioErrorElement) {
                        this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            }
            if (!this.statute_accept_option) {
                this.radioError = 'Proszę zaakceptować wszystkie wymagane zgody.';
                valid = false;
                this.$nextTick(() => {
                    if (this.$refs.radioErrorElement) {
                        this.$refs.radioErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            }


            if (valid) {
                const captchaToken = await this.resolveRecaptchaToken();
                if (this.shouldRequireRecaptcha() && !captchaToken) {
                    return;
                }
                await this.sendAdditionalInfo(captchaToken);
            }
        },
        openTimePicker(event) {
            event.target.showPicker(); // Wymusza otwarcie wbudowanego wyboru czasu
        },
        totalBrutto(price, quantity, days) {
            const net = this.totalNet(price, quantity, days) * 1.23;
            return net.toFixed(2); // Dodanie 23% do ceny netto
        },
        async sendAdditionalInfo(captchaToken = '') {
            this.isSending = true;
            this.submitError = '';
            const payload = {
                "email": this.email,
                "recaptcha_token": captchaToken || null,
                "imie_nazwisko": this.firstName + ' ' + this.lastName,
                "data_transparent": {
                    "data_zamowienia": new Date().toLocaleDateString(),
                    "godzina_zamowienia": new Date().toLocaleTimeString(),
                    "numer_zamowienia": new Date().toLocaleDateString() + '_' + new Date().toLocaleTimeString(),

                    "metoda_dostawy": this.selectedOption === 'delivery' ? 'Dostawa' : 'Odbiór osobisty',
                    "data_wypozyczenia": this.cartStore.add_information.deliveryDate ? this.cartStore.add_information.deliveryDate.toLocaleDateString() : 'Nie podano',
                    "data_zwrotu": this.cartStore.add_information.pickupDate ? this.cartStore.add_information.pickupDate.toLocaleDateString() : 'Nie podano',

                    "imie_nazwisko_dostawy": this.firstNameDelivery + ' ' + this.lastNameDelivery,
                    "nazwa_firmy_dostawy": this.companyNameDelivery,
                    "adres_dostawy": this.companyAddressDelivery,
                    "miejscowosc_dostawy": this.cityDelivery,
                    "kod_pocztowy_dostawy": this.postalCodeDelivery,
                    "telefon_dostawy": this.phoneNumberDelivery,
                    "data_dostawy": this.cartStore.add_information.deliveryDate ? this.cartStore.add_information.deliveryDate.toLocaleDateString() + ' ' + this.cartStore.add_information.deliveryTime : 'Nie podano',
                    "data_odbioru": this.cartStore.add_information.pickupDate ? this.cartStore.add_information.pickupDate.toLocaleDateString() + ' ' + this.cartStore.add_information.pickupTime : 'Nie podano',
                    "godzina_dostawy": this.cartStore.add_information.deliveryTime || 'Nie podano',
                    "godzina_odbioru": this.cartStore.add_information.pickupTime || 'Nie podano',

                    "imie_nazwisko": this.firstName + ' ' + this.lastName,
                    "nazwa_firmy": this.companyName || 'Brak firmy',
                    "adres": this.companyAddress,
                    "miejscowosc": this.city,
                    "kod_pocztowy": this.postalCode,
                    "telefon": this.phoneNumber,

                    "nip": this.companyNip || 'Brak NIP',
                    "produkty": this.cartStore.items.map(item => ({
                        "product_id": item.id,
                        "zdjecie": process.env.VUE_APP_IMAGE_URL + encodeURIComponent(item.meta.images[0]),
                        "nazwa": item.name || 'Brak nazwy',
                        "ilosc": item.quantity || 1,
                        "cena_netto": item.rental_price || 0,
                        "suma_netto": (item.quantity || 1) * (item.rental_price || 0) * (item.days || 1),
                        "ilosc_dni": item.days || 1,
                        "suma_brutto": (item.quantity || 1) * (item.rental_price || 0) * (item.days || 1) * 1.23,
                    })),
                    "suma_netto_total": this.cartStore.totalNet,
                    "suma_brutto_total": this.cartStore.totalBrutto
                }
            }

            try {
                // Wysłanie danych do API
                const apiUrl = process.env.VUE_APP_API_URL;
                // const apiUrl = "https://l4887ivvna.execute-api.eu-central-1.amazonaws.com/dev";
                const response = await axios.post(`${apiUrl}/email`, payload);
                if (!this.isBackendSubmissionAccepted(response)) {
                    const rejectReason = this.getBackendRejectReason(response);
                    const rejectError = new Error('backend-rejected-submission');
                    rejectError.backendReason = rejectReason;
                    throw rejectError;
                }
                console.log('Dziękujemy za zapytanie. Informacje zostały wysłane.');
                this.registerSuccessfulSubmissionEmail();

                // Wyczyszczenie pól po udanym wysłaniu
                this.selectedOption = '';
                this.cartStore.add_information.deliveryDate = null;
                this.cartStore.add_information.pickupDate = null;
                this.cartStore.add_information.deliveryTime = '';
                this.cartStore.add_information.pickupTime = '';
                this.firstName = '';
                this.lastName = '';
                this.email = '';
                this.phoneNumber = '';
                this.firstNameDelivery = '';
                this.lastNameDelivery = '';
                this.city = '';
                this.postalCode = '';
                this.companyAddressDelivery = '';
                this.cityDelivery = '';
                this.postalCodeDelivery = '';
                this.subject = '';
                this.message = '';
                this.cartStore.clearCart();
                this.setDefaultTimesIfMissing();
                this.recaptchaToken = '';
                this.recaptchaError = '';
                this.resetRecaptchaWidget();

                this.successMessage = true;

            } catch (error) {
                console.error('Error sending request for product rental:', error);
                const status = error?.response?.status;
                const backendReason = error?.backendReason || error?.response?.data?.reason || '';
                if (status === 403 || status === 400 || error?.message === 'backend-rejected-submission') {
                    await this.enforceRecaptchaFromBackend(backendReason);
                    this.submitError = this.mapBackendRejectReason(backendReason);
                    return;
                }
                this.submitError = 'Nie udało się wysłać formularza. Spróbuj ponownie za chwilę.';
            } finally {
                this.isSending = false;
            }
        }

    },
    mounted() {
        this.normalizeStoreDates();
        this.setDefaultTimesIfMissing();
        this.syncRecaptchaRequirement();
    }
};
</script>

<style module>
input {
    font-family: 'MyFont', sans-serif;
    text-align: left;
    padding-left: 10px;
    text-transform: uppercase;
    box-sizing: border-box;
    width: 100%;
    /* height: 40px; */
    font-size: 10pt;
    border: 2px solid var(--dark-color-background);
    background-color: var(--dark-color-font);
    border-radius: 2px;
}
</style>


<style scoped>
* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}



body,
html {
    height: 100%;
    overflow-x: hidden;
}

.shop {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding-top: 250px;
    box-sizing: border-box;
    background-color: var(--dark-color-font);
    color: var(--dark-color-background);
    width: 100%;
    height: 100%;
    padding-left: 30px;
    padding-right: 30px;
}

.shop .header {
    width: 90%;
    max-width: 1100px;
    text-align: left;
    font-size: 8pt;
    white-space: pre-line;
}

.shop .header .selected-category {
    color: var(--dark-color-background);
    margin: 0;
    margin-bottom: 40px;
    font-size: 14pt;
}

.shop .header span {
    color: var(--dark-color-background);
}

.shop .header p {
    margin-top: 20px;
    margin-bottom: 20px;
}

.shop .header .warning {
    padding-top: 20px;
    color: var(--dark-color-red);
}

.shop .header .trans {
    color: red;
    margin-left: 20px;
    font-size: 10px;
    margin-top: 5px;
}

input:disabled {
    background-color: rgba(0, 0, 0, 0.1);
    /* Półprzezroczysty szary */
    color: rgba(0, 0, 0, 0.5);
    /* Przygaszony kolor tekstu */
    border-color: rgba(0, 0, 0, 0.3);
    /* Przygaszona obwódka */
    cursor: not-allowed;
    /* Zmiana kursora na "zakaz" */
}


.radio-group {
    display: flex;
    gap: 20px;
    margin-top: 20px;
    margin-bottom: 40px;
}

.radio-option {
    display: flex;
    align-items: center;
}

.radio-option input[type="radio"] {
    display: none;
}

.custom-radio {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid var(--dark-color-background);
    border-radius: 2px;
    margin-right: 8px;
    position: relative;
    cursor: pointer;
}

.radio-option input[type="radio"]:checked+.custom-radio {
    background-color: var(--dark-color-background);
}

.custom-radio::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 8px;
    height: 8px;
    background-color: var(--dark-color-background);
    transform: translate(-50%, -50%);
    opacity: 0;
}

.radio-option input[type="radio"]:checked+.custom-radio::after {
    opacity: 1;
}

.checkbox-group {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-top: 20px;
}

.custom-checkbox {
    display: flex;
    align-items: center;
    position: relative;
    cursor: pointer;
    font-size: 11px;
    user-select: none;
    min-height: 24px;
}

.custom-checkbox input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
}

.checkmark {
    display: inline-block;
    width: 20px;
    height: 20px;
    background-color: var(--dark-color-font);
    border: 2px solid var(--dark-color-background);
    margin-right: 10px;
    flex-shrink: 0;
}

.custom-checkbox input:checked~.checkmark {
    background-color: var(--dark-color-background);
}

::v-deep .v3dp__popout {
    left: 2px;
    width: 100% !important;
    max-width: none !important;
}

::v-deep .v3dp__input_wrapper {
    width: 100%;
}

::v-deep .v3dp__input_wrapper input {
    width: 100%;
}

.delivery-pickup-form {
    width: 100%;
    max-width: 1400px;
    font-size: 16px;
}

.delivery-pickup-form p {
    font-size: 12px;
    color: var(--dark-color-red);
    margin-bottom: 10px;
}

.form-container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
}

.form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
}

.form-group {
    display: flex;
    flex-direction: column;
}

.form-group.full-width {
    grid-column: span 2;
}

label {
    margin-bottom: 5px;
}

input {
    font-family: 'MyFont', sans-serif;
    text-align: left;
    padding: 10px;
    border: 2px solid var(--dark-color-background);
    border-radius: 2px;
    background-color: var(--dark-color-font);
}


.custom-time-picker {
    position: relative;
    width: 100%;
}

.custom-time-picker input {
    height: 40px;
}

.dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    color: var(--dark-color-background);
    background-color: var(--dark-color-font);
    border: 1px solid var(--dark-border-color);
    max-height: 150px;
    overflow-y: auto;
    width: 100%;
    z-index: 1000;
}

.dropdown li {
    padding: 5px 10px;
    cursor: pointer;
}

.dropdown li:hover {
    background-color: var(--dark-color-background);
    color: var(--dark-color-font);
}

.cart-summary {
    font-size: 16pt;
    display: flex;
    justify-content: flex-end;
    flex-direction: column;
    margin-top: 40px;
    width: 70%;
    margin-left: auto;
    /* Dodane, aby wypchnąć element na prawą stronę */
}

.cart-summary button {
    margin-top: 40px;
    font-size: 16pt;
    padding: 10px 20px;
    background-color: var(--dark-color-background);
    border: 2px solid transparent;
    transition: background 0.3s, color 0.3s;
}

.cart-summary button:hover:not(:disabled) {
    background-color: var(--dark-color-font);
    color: var(--dark-color-background);
    border: 2px solid var(--dark-color-background);
}

.cart-summary button:disabled {
    cursor: not-allowed;
    opacity: 0.85;
}

.sending-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.5);
}

.sending-spinner {
    width: 60px;
    height: 60px;
    border: 8px solid rgba(255, 255, 255, 0.25);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.summary {

    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: 10px;

    border-bottom: 2px solid var(--dark-color-background);
    padding-bottom: 10px;
}

.summary .price {
    width: 25%;
    font-size: 14pt;
    border-left: 2px solid var(--dark-color-background);
    padding-left: 15px;
    color: var(--dark-color-red);
}

.summary .sum {
    width: 50%;
}


.error-message {
    color: red !important;
    font-size: 14px !important;
}

.captcha-note {
    margin-top: 16px;
    color: var(--dark-color-red);
    font-size: 12px;
}

.recaptcha-wrapper {
    margin-top: 12px;
}

.recaptcha-box {
    min-height: 78px;
    min-width: 304px;
}



.shop .success_message {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    max-width: 1100px;
    margin-top: 50px;
    /* Odstęp od góry */
}

.shop .success-content {
    flex: 1;
    text-align: left;
}

.shop .success-title {
    font-size: 24px;
    /* Zwiększenie rozmiaru tekstu */
    font-weight: bold;
    margin-bottom: 30%;
}

.shop .success-subtitle {
    font-size: 16pt;
    color: var(--dark-color-background);
    /* Zmiana koloru napisu */
    margin-bottom: 20px;
}

.shop .success-subtitle-1 {
    font-size: 10px;
    color: var(--dark-color-background);
    /* Zmiana koloru napisu */
    margin-bottom: 20px;
}

.shop .success-button {
    padding: 15px 30px;
    background-color: var(--dark-color-background);
    /* Ciemne tło przycisku */
    color: var(--dark-color-font);
    /* Kolor tekstu */
    border: 2px solid transparent;
    border-radius: 2px;
    cursor: pointer;
    text-transform: uppercase;
    font-size: 14px;
    width: 70%;
    font-weight: bold;
    transition: background-color 0.3s;
}

.shop .success-button:hover {
    background-color: var(--dark-color-font);
    color: var(--dark-color-background);
    border: 2px solid var(--dark-color-background);
    transition: background 0.3s, color 0.3s;
    /* Zmiana koloru tła przycisku po najechaniu */
}

.shop .success-image img {
    max-width: 500px;
    /* Ustawienie szerokości obrazka */
    height: auto;
}


@media (max-width: 800px) {

    ::v-deep .v3dp__input_wrapper {
        font-size: 14px;
    }

    ::v-deep .v3dp__input_wrapper input {
        font-size: 10px;
    }

    .shop .header .trans {
        color: red;
        margin-left: 10px;
        font-size: 9px;
        margin-top: 5px;
    }

    .shop {
        padding: 20px;
        padding-top: 120px;
        display: flex;
        flex-direction: column;
        align-items: center;
        /* Wyśrodkowanie poziome */
        justify-content: center;
        /* Wyśrodkowanie pionowe */
        min-height: 100vh;
        /* Minimalna wysokość na 100% okna przeglądarki */
        box-sizing: border-box;
    }

    .form-grid {
        display: flex;
        flex-direction: column;
        /* Układ kolumnowy dla wąskich ekranów */
        gap: 20px;
        font-size: 10px;
    }

    .form-group {
        width: 100%;
        /* Ustawienie pełnej szerokości dla wszystkich pól */
    }

    .form-group.full-width {
        width: 100%;
        /* Pełna szerokość również dla pełnych pól */
    }

    .radio-group {
        display: flex;
        flex-direction: column;
        /* Układ w kolumnie */
        gap: 10px;
        /* Odstęp między opcjami */
    }

    .radio-option {
        display: flex;
        align-items: center;
        position: relative;
        cursor: pointer;
        font-size: 10px;
        /* Dopasowanie rozmiaru tekstu */
        user-select: none;
        min-height: 24px;
    }

    .radio-option input[type="radio"] {
        position: absolute;
        opacity: 0;
        cursor: pointer;
        height: 16px;
        width: 16px;
    }

    input,
    textarea,
    .custom-time-picker input {
        width: 100%;
        /* Wszystkie pola na pełną szerokość */
        font-size: 10px;
        /* Mniejsza czcionka dla pól */
        box-sizing: border-box;
        /* Uwzględnienie paddingu w szerokości */
    }

    label {
        font-size: 10px;
        /* Mniejsza czcionka dla etykiet */
    }


    .custom-radio {
        display: inline-block;
        width: 16px;
        /* Rozmiar kwadratu */
        height: 16px;
        /* Rozmiar kwadratu */
        background-color: var(--dark-color-font);
        border: 2px solid var(--dark-color-background);
        margin-right: 10px;
        flex-shrink: 0;
        position: relative;
    }

    .radio-option input[type="radio"]:checked~.custom-radio {
        background-color: var(--dark-color-background);
    }

    .custom-radio::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 8px;
        height: 8px;
        background-color: var(--dark-color-font);
        transform: translate(-50%, -50%);
        opacity: 0;
    }

    .radio-option input[type="radio"]:checked~.custom-radio::after {
        opacity: 1;
        /* Widoczny punkt w środku po zaznaczeniu */
    }

    .checkbox-group {
        display: flex;
        flex-direction: column;
        /* Układ w kolumnie */
        gap: 10px;
    }

    .custom-checkbox {
        display: flex;
        align-items: center;
        position: relative;
        cursor: pointer;
        font-size: 10px;
        /* Rozmiar tekstu */
        user-select: none;
    }

    .custom-checkbox input {
        position: absolute;
        opacity: 0;
        cursor: pointer;
        height: 16px;
        width: 16px;
    }

    .checkmark {
        display: inline-block;
        width: 16px;
        /* Rozmiar kwadratu */
        height: 16px;
        /* Rozmiar kwadratu */
        background-color: var(--dark-color-font);
        border: 2px solid var(--dark-color-background);
        margin-right: 10px;
    }

    .custom-checkbox input:checked~.checkmark {
        background-color: var(--dark-color-background);
    }

    .cart-summary {
        font-size: 12px;
        display: flex;
        justify-content: flex-end;
        flex-direction: column;
        margin-top: 20px;
        width: 100%;
    }

    .summary .price {
        font-size: 12px;
    }

    .cart-summary button {
        margin-top: 20px;
        font-size: 14px;
        padding: 10px 20px;
    }
}
</style>